import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  QueryConstraint,
  startAfter,
  updateDoc,
  where,
} from "firebase/firestore/lite";
import { EPostStatus, EPostType, TPost, TPostAd } from "../models/posts.model";
import { apiWorker, TResponse } from "./apiWorker";

export function getPostAd({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: TResponse<TPostAd>) => void;
  onError?: (data: any) => void;
}) {
  apiWorker({
    url: `/posts/ad`,
    method: "GET",
    onSuccess,
    onError,
  });
}

export async function getPosts({
  parents,
  offset,
  limit: _limit,
  type,
  status,
  order,
  onSuccess,
  onError,
}: {
  parents?: string[];
  offset?: number;
  limit?: number;
  type?: EPostType;
  status?: EPostStatus;
  order?: "asc" | "desc";
  onSuccess?: (data: TResponse<TPost>) => void;
  onError?: (data: any) => void;
}) {
  const db = getFirestore();

  const uid = localStorage.getItem("uid");

  const constraints: QueryConstraint[] = [where("authorId", "==", uid)];

  if (parents && parents.length > 0) {
    constraints.push(where("parents", "array-contains-any", parents));
  }

  if (type) {
    constraints.push(where("type", "==", type));
  }

  if (status) {
    constraints.push(where("status", "==", status));
  }

  if (order) {
    constraints.push(orderBy("createdAt", order));
  }

  if (offset) {
    constraints.push(startAfter(offset));
  }

  if (_limit) {
    constraints.push(limit(_limit));
  }

  const queried = query(collection(db, "events"), ...constraints);

  const { docs } = await getDocs(queried);

  const posts = docs.map((doc) => {
    const data = doc.data() as TPost;
    return {
      ...data,
      id: doc.id,
    };
  });

  onSuccess?.({
    content: posts,
    total: posts.length,
    limit: _limit || 0,
    offset: offset || 0,
  });
}

export function createPost({
  content,
  interests,
  medias,
  status,
  title,
  eventDate,
  timelineId,
  parents,
  type,
  metadata,
  onSuccess,
  onError,
}: {
  title: string;
  type: EPostType;
  content: any;
  status: EPostStatus;
  interests: string[];
  medias: string[];
  eventDate?: Date;
  timelineId?: string;
  parents?: string[];
  metadata?: any;
  onSuccess?: (data: TPost) => void;
  onError?: (data: any) => void;
}) {
  const db = getFirestore();
  const uid = localStorage.getItem("uid");
  const docRef = collection(db, "events");

  const docData = {
    content,
    interests,
    medias,
    status,
    title,
    eventDate: eventDate?.toISOString() || null,
    timelineId: timelineId || null,
    parents: parents || [],
    type,
    metadata,
    authorId: uid || "",

    isEdited: false,
    editedAt: null,

    integrationId: null,
    integrationStore: null,
    externalId: null,

    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  addDoc(docRef, docData)
    .then((doc) => {
      console.log("Document written with ID: ", doc.id);

      const data: TPost = {
        ...docData,
        id: doc.id || "",
      };

      onSuccess && onSuccess(data);
    })
    .catch((error) => {
      console.error("Error adding document: ", error);

      onError && onError(error);
    });
}

export function updatePost({
  postId,
  content,
  interests,
  medias,
  status,
  title,
  eventDate,
  timelineId,
  parents,
  type,
  metadata,
  onSuccess,
  onError,
}: {
  postId: string;
  title: string;
  type: EPostType;
  content: any;
  status: EPostStatus;
  interests: string[];
  medias: string[];
  eventDate?: Date;
  timelineId?: string;
  parents?: string[];
  metadata?: any;
  onSuccess?: (data: TPost) => void;
  onError?: (data: any) => void;
}) {
  const db = getFirestore();
  const docData = {
    content,
    interests,
    medias,
    status,
    title,
    eventDate: eventDate?.toISOString() || null,
    timelineId: timelineId || null,
    parents: parents || [],
    type,
    metadata,

    isEdited: true,
    editedAt: new Date().toISOString(),

    integrationId: null,
    integrationStore: null,
    externalId: null,

    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  updateDoc(doc(db, "events", postId), docData)
    .then(() => {
      console.log("Document updated with ID: ", postId);

      const data: TPost = {
        ...docData,
        authorId: localStorage.getItem("uid") || "",
        id: postId || "",
      };

      onSuccess && onSuccess(data);
    })
    .catch((error) => {
      console.error("Error updating document: ", error);

      onError && onError(error);
    });
}

export function deletePost({
  postId,
  onSuccess,
  onError,
}: {
  postId: string;
  onSuccess?: (data: TPost) => void;
  onError?: (data: any) => void;
}) {
  const db = getFirestore();
  const docRef = doc(db, "events", postId);

  deleteDoc(docRef)
    .then(() => {
      console.log("Document deleted with ID: ", postId);

      onSuccess && onSuccess({} as TPost);
    })
    .catch((error) => {
      console.error("Error deleting document: ", error);

      onError && onError(error);
    });
}
